import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";
import "./CustomSelect.css";

export default function CustomSelect({
  value,
  onChange,
  options = [],
  disabled = false,
  placeholder = "Select...",
  className = "",
  dropdownClassName = "",
  searchable = true,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const positionFrameRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    function onClickOutside(e) {
      const insideRoot = rootRef.current?.contains(e.target);
      const insideDropdown = dropdownRef.current?.contains(e.target);
      if (!insideRoot && !insideDropdown) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const selected = options.find((o) => o.value === value);
  const filteredOptions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const visibleOptions = term
      ? options.filter((opt) =>
          String(opt.label || opt.value || "")
            .toLowerCase()
            .includes(term),
        )
      : options;

    const selectedOption = visibleOptions.find((opt) => opt.value === value);
    const remainingOptions = visibleOptions.filter((opt) => opt.value !== value);

    return selectedOption ? [selectedOption, ...remainingOptions] : remainingOptions;
  }, [options, searchTerm, value]);

  useEffect(() => {
    if (!open || !triggerRef.current) return undefined;

    const updatePosition = () => {
      const rect = triggerRef.current.getBoundingClientRect();
      const viewportPadding = 12;
      const totalMaxHeight = Math.min(240, Math.max(180, window.innerHeight - viewportPadding * 2));
      const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
      const spaceAbove = rect.top - viewportPadding;
      const openUpward = spaceBelow < 240 && spaceAbove > spaceBelow;
      const availableHeight = Math.max(
        120,
        Math.min(totalMaxHeight, openUpward ? spaceAbove - 4 : spaceBelow - 4),
      );
      const optionHeight = 44;
      const dropdownPadding = 8;
      const optionGap = 4;
      const emptyStateHeight = 44;
      const contentHeight =
        filteredOptions.length > 0
          ? filteredOptions.length * optionHeight +
            Math.max(0, filteredOptions.length - 1) * optionGap +
            dropdownPadding
          : emptyStateHeight + dropdownPadding;
      const dropdownHeight = Math.min(availableHeight, contentHeight);
      const baseTop = openUpward
        ? Math.max(viewportPadding, rect.top - dropdownHeight - 4)
        : rect.bottom + 4;

      setDropdownStyle({
        top: baseTop,
        left: rect.left,
        width: rect.width,
        maxHeight: dropdownHeight,
      });

      if (positionFrameRef.current) {
        window.cancelAnimationFrame(positionFrameRef.current);
      }

      positionFrameRef.current = window.requestAnimationFrame(() => {
        const actualHeight = dropdownRef.current?.getBoundingClientRect().height;
        if (!actualHeight) {
          positionFrameRef.current = null;
          return;
        }

        const measuredTop = openUpward
          ? Math.max(viewportPadding, rect.top - actualHeight - 4)
          : rect.bottom + 4;

        setDropdownStyle((current) => {
          if (!current) return current;
          if (Math.abs(Number(current.top) - measuredTop) < 1) return current;

          return {
            ...current,
            top: measuredTop,
          };
        });

        positionFrameRef.current = null;
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      if (positionFrameRef.current) {
        window.cancelAnimationFrame(positionFrameRef.current);
        positionFrameRef.current = null;
      }
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [filteredOptions.length, open]);

  useEffect(() => {
    if (!open) {
      setSearchTerm("");
      return;
    }

    searchInputRef.current?.focus();
  }, [open]);

  function handleSelect(val) {
    onChange(val);
    setOpen(false);
  }

  return (
    <div
      ref={rootRef}
      className={["cs-root", open ? "cs-open" : "", disabled ? "cs-disabled" : "", className]
        .filter(Boolean)
        .join(" ")}
    >
      {open ? (
        searchable ? (
          <div ref={triggerRef} className="cs-trigger cs-trigger-searching">
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              className="cs-inline-search"
              placeholder={selected ? selected.label : placeholder}
            />
            <button
              type="button"
              className="cs-chevron-btn"
              onClick={() => setOpen(false)}
              aria-label="Close select"
            >
              <ChevronDown className="cs-chevron" />
            </button>
          </div>
        ) : (
          <button
            ref={triggerRef}
            type="button"
            className="cs-trigger"
            onClick={() => setOpen(false)}
          >
            <span className={`cs-value${!selected ? " cs-value-placeholder" : ""}`}>
              {selected ? selected.label : placeholder}
            </span>
            <ChevronDown className="cs-chevron" />
          </button>
        )
      ) : (
        <button
          ref={triggerRef}
          type="button"
          className="cs-trigger"
          onClick={() => {
            if (!disabled) setOpen((o) => !o);
          }}
        >
          <span className={`cs-value${!selected ? " cs-value-placeholder" : ""}`}>
            {selected ? selected.label : placeholder}
          </span>
          <ChevronDown className="cs-chevron" />
        </button>
      )}

      {open &&
        dropdownStyle &&
        createPortal(
          <div
            ref={dropdownRef}
            className={["cs-dropdown", dropdownClassName].filter(Boolean).join(" ")}
            style={dropdownStyle}
          >
            <div className="cs-options">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`cs-option${opt.value === value ? " cs-option-selected" : ""}`}
                    onClick={() => handleSelect(opt.value)}
                  >
                    {opt.label}
                  </button>
                ))
              ) : (
                <div className="cs-empty">No results found.</div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
